import { FullSizeSpinner } from '@/components/full-size-spinner';
import HeaderInfo from '@/components/page-header/header-info';
import { calculateHasFutureLpChange } from '@/components/table/table-helpers';
import { useErrorNotification } from '@/hooks/use-error-notification';
import { connectColors } from '@/theme/colors';
import { Container, Flex, Text } from '@lego/klik-ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import CreatePriceTable from './components/create-price-table';
import { SessionRecoveryModal } from './components/session-recovery-modal';
import { CREATE_PRICE_PUBLISHED_ROWS_STORAGE_KEY } from './create-price-constants';
import {
  getHeaderNames,
  isApproveCreatePriceMode,
  mergeSessionWithFreshData,
  sessionQueryParametersToFilters,
} from './create-price-helpers';
import {
  useCreatePriceExperience,
  useDeleteCreatePriceSession,
  useGetCreatePriceSession,
} from './create-price-queries';
import {
  type CreatePriceMode,
  CreatePriceQueryParams,
  decodeCreatePriceParams,
} from './create-price-search-params';
import type { HeaderNames, RowData } from './types/create-price-table-types';
import { PageWrapper } from '@/components/page-wrapper/page-wrapper';

const CreatePricePage = () => {
  const [searchParams] = useSearchParams();
  const { state } = useLocation();
  const typedState = state as {
    headerNames?: HeaderNames;
    refetchKey?: number;
  } | null;
  const stateHeaderNames = typedState?.headerNames;
  const refetchKey = typedState?.refetchKey;

  const { mode, filters, fromSession, published } =
    decodeCreatePriceParams(searchParams);

  // Exclude session-only flags so table data does not refetch when only those flags change.
  const filterQueryKey = useMemo(() => {
    const params = new URLSearchParams(searchParams);
    params.delete(CreatePriceQueryParams.FromSession);
    params.delete(CreatePriceQueryParams.Published);
    return params.toString();
  }, [searchParams]);

  // Stable filters reference — only changes when filter-defining query params change.
  const stableFilters = useMemo(
    () => decodeCreatePriceParams(new URLSearchParams(filterQueryKey)).filters,
    [filterQueryKey],
  );

  const isApproveMode = isApproveCreatePriceMode(mode);

  const showError = useErrorNotification();
  const {
    mutate: createPriceExperience,
    data: rawData,
    isPending: isLoading,
  } = useCreatePriceExperience((error) => showError(error, 'load prices'));

  const { data: sessionData } = useGetCreatePriceSession(mode);
  const { mutate: deleteSession, isPending: isDiscarding } =
    useDeleteCreatePriceSession(mode);

  const sessionRecoveryTriggerRef = useRef<HTMLElement | null>(null);

  const [sessionAccepted, setSessionAccepted] = useState(
    fromSession || published,
  );
  const [sessionDismissed, setSessionDismissed] = useState(false);

  const fetchPrices = useCallback(() => {
    createPriceExperience({
      ...stableFilters,
      priceUnitCategoryId: stableFilters.priceUnitCategoryId ?? undefined,
      priceUnitIds:
        stableFilters.priceUnitCategoryId != null
          ? undefined
          : stableFilters.priceUnitIds,
    });
  }, [createPriceExperience, stableFilters]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: refetchKey triggers refetch for same-filter resubmissions
  useEffect(() => {
    fetchPrices();
  }, [fetchPrices, refetchKey]);

  const freshData: Map<string, RowData> = useMemo(() => {
    if (!rawData) return new Map();
    return new Map(
      rawData.map((item) => [
        item.primaryKey,
        {
          ...item,
          hasPriceChange: false,
          hasFutureLpChange: calculateHasFutureLpChange(
            item.lp,
            item.nearestFutureModifiedLp,
          ),
          rrpCurrency: item.currency ?? '',
        },
      ]),
    );
  }, [rawData]);

  const hasSessionRows = (sessionData?.modifiedRows?.length ?? 0) > 0;
  const sessionMatchesMode =
    !!sessionData &&
    hasSessionRows &&
    (sessionData.mode as CreatePriceMode) === mode;

  // Auto-accept session if filters match (user refreshed on same page)
  const sessionMatchesFilters = useMemo(() => {
    if (!sessionData?._queryParameters) return false;
    const sessionFilters = sessionQueryParametersToFilters(
      sessionData._queryParameters,
    );
    return (
      JSON.stringify(sessionFilters, (_, v) =>
        v instanceof Date ? v.toISOString() : v,
      ) ===
      JSON.stringify(filters, (_, v) =>
        v instanceof Date ? v.toISOString() : v,
      )
    );
  }, [sessionData, filters]);

  const showRecoveryModal =
    sessionMatchesMode &&
    !sessionAccepted &&
    !sessionDismissed &&
    !published &&
    !sessionMatchesFilters;

  const publishedRows = useMemo(() => {
    if (!published) return null;
    try {
      const stored = sessionStorage.getItem(
        CREATE_PRICE_PUBLISHED_ROWS_STORAGE_KEY,
      );
      if (stored) return JSON.parse(stored);
    } catch (e) {
      console.warn('Failed to parse published rows:', e);
    }
    return null;
  }, [published]);

  const data: Map<string, RowData> = useMemo(() => {
    if (freshData.size === 0) return new Map();

    if (published && publishedRows && rawData) {
      return mergeSessionWithFreshData(rawData, publishedRows, true);
    }

    if (
      !published &&
      sessionMatchesMode &&
      (sessionAccepted || sessionMatchesFilters) &&
      !sessionDismissed &&
      rawData &&
      sessionData?.modifiedRows
    ) {
      return mergeSessionWithFreshData(rawData, sessionData.modifiedRows);
    }

    return freshData;
  }, [
    freshData,
    published,
    publishedRows,
    sessionMatchesMode,
    sessionMatchesFilters,
    sessionAccepted,
    sessionDismissed,
    rawData,
    sessionData,
  ]);

  const handleSessionContinue = useCallback(() => {
    setSessionAccepted(true);
  }, []);

  const handleSessionDiscard = useCallback(() => {
    deleteSession(undefined, {
      onSettled: () => {
        setSessionAccepted(false);
        setSessionDismissed(true);
        fetchPrices();
      },
    });
  }, [deleteSession, fetchPrices]);

  const headerNames = useMemo<HeaderNames | undefined>(() => {
    if (stateHeaderNames) return stateHeaderNames;
    if (!rawData || rawData.length === 0) return undefined;
    return getHeaderNames(rawData, filters);
  }, [stateHeaderNames, rawData, filters]);

  return (
    <PageWrapper
      headerProps={{
        title: isApproveMode ? 'Approve prices' : 'Create prices',
        actions:
          data.size > 0 ? (
            <Flex flexDirection={'row'}>
              <Flex
                flexDirection={'column'}
                padding={'0 16px'}
                borderLeft={`1px solid ${connectColors.transparent.slate[700]}`}
              >
                <Text
                  color={connectColors.core.offBlack}
                  className="body-sm-medium"
                >
                  {filters.validFromDate?.toLocaleDateString('sv-SE')}
                </Text>
              </Flex>

              {headerNames?.priceUnits && headerNames.priceUnits.length > 0 && (
                <HeaderInfo data={headerNames.priceUnits.join(', ')} />
              )}
              {headerNames?.products && headerNames.products.length > 0 && (
                <HeaderInfo data={headerNames.products.join(', ')} />
              )}
              {headerNames?.themes && headerNames.themes.length > 0 && (
                <HeaderInfo data={headerNames.themes.join(', ')} />
              )}
              {headerNames?.groups && headerNames.groups.length > 0 && (
                <HeaderInfo data={headerNames.groups.join(', ')} />
              )}
            </Flex>
          ) : null,
      }}
      maxWidth="100%"
      contentPaddingX={0}
      layout="fill"
    >
      <Container maxW="100%" padding={0}>
        {isLoading && <FullSizeSpinner />}
        {!isLoading && data.size === 0 && (
          <Flex
            justifyContent="center"
            alignItems="center"
            height="100%"
            minHeight="300px"
            flexDirection="column"
            gap="16px"
          >
            <Text
              fontSize="lg"
              fontWeight="medium"
              color={connectColors.core.offBlack}
            >
              {published
                ? isApproveMode
                  ? 'All prices approved'
                  : 'All prices published'
                : 'No prices to approve'}
            </Text>
            <Text color={connectColors.core.slate[600]}>
              {published
                ? 'Use the navigation to start a new session.'
                : 'There are no preliminary prices matching your filter criteria.'}
            </Text>
          </Flex>
        )}
        {data.size > 0 && filters.validFromDate && (
          <CreatePriceTable
            initialData={data}
            originalData={
              sessionAccepted || sessionMatchesFilters ? freshData : undefined
            }
            validFrom={filters.validFromDate}
            mode={mode}
            filters={filters}
            isPublished={published}
          />
        )}
      </Container>
      {sessionData && showRecoveryModal && (
        <SessionRecoveryModal
          isOpen={showRecoveryModal}
          triggerRef={sessionRecoveryTriggerRef}
          onClose={() => setSessionDismissed(true)}
          onContinue={handleSessionContinue}
          onDiscard={handleSessionDiscard}
          isDiscarding={isDiscarding}
          session={sessionData}
        />
      )}
    </PageWrapper>
  );
};
export default CreatePricePage;
